import { useEffect, useState } from 'react';
import { Code, Eye, Maximize2, Minimize2, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  ELEMENT_KINDS,
  elementKindLabel,
  elementSlugFromName,
  isValidElementSlug,
  type ContentElementRow,
  type ElementKind,
} from '../../lib/contentElements';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

type HtmlPane = 'edit' | 'preview';

export default function ElementModal({
  isOpen,
  onClose,
  element,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  element: ContentElementRow | null;
  onSuccess: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ElementKind>('other');
  const [description, setDescription] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [htmlPane, setHtmlPane] = useState<HtmlPane>('edit');

  useEffect(() => {
    if (!isOpen) return;
    setExpanded(false);
    setHtmlPane('edit');
    if (element) {
      setSlug(element.slug);
      setName(element.name);
      setKind(element.kind);
      setDescription(element.description ?? '');
      setBodyHtml(element.body_html);
      setSortOrder(element.sort_order);
      setActive(element.active);
    } else {
      setSlug('');
      setName('');
      setKind('other');
      setDescription('');
      setBodyHtml('');
      setSortOrder(0);
      setActive(true);
    }
  }, [isOpen, element]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleNameBlur = () => {
    if (!element && name.trim() && !slug.trim()) {
      setSlug(elementSlugFromName(name));
    }
  };

  const handleSave = async () => {
    const normalizedSlug = elementSlugFromName(slug || name);
    if (!isValidElementSlug(normalizedSlug)) {
      toast.error('Slug must be 2–64 chars: lowercase letters, digits, underscores');
      return;
    }
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!bodyHtml.trim()) {
      toast.error('HTML body is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        slug: normalizedSlug,
        name: name.trim(),
        kind,
        description: description.trim() || null,
        body_html: bodyHtml,
        sort_order: sortOrder,
        active,
        updated_at: new Date().toISOString(),
      };

      if (element) {
        const { error } = await supabase.from('elements').update(payload).eq('id', element.id);
        if (error) throw error;
        toast.success('Element updated');
      } else {
        const { error } = await supabase.from('elements').insert(payload);
        if (error) throw error;
        toast.success('Element created');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const panelClass = expanded
    ? 'relative z-[71] w-[min(96vw,80rem)] h-[min(92vh,900px)] flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden'
    : 'relative z-[71] w-full max-w-2xl flex flex-col border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl';

  const htmlEditorMinH = expanded ? 'min-h-[min(52vh,520px)]' : 'min-h-[220px]';

  return (
    <div className="fixed inset-0 z-[70] flex items-stretch sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 w-full h-full bg-black/50"
        aria-label="Close"
        onClick={onClose}
      />
      <div className={panelClass}>
        <div className="shrink-0 px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 bg-white dark:bg-gray-800">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            {element ? 'Edit element' : 'New element'}
          </h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              title={expanded ? 'Smaller window' : 'Larger window'}
            >
              {expanded ? (
                <>
                  <Minimize2 className="w-3.5 h-3.5" />
                  Compact
                </>
              ) : (
                <>
                  <Maximize2 className="w-3.5 h-3.5" />
                  Expand
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div
          className={`flex-1 min-h-0 overflow-hidden ${
            expanded ? 'flex flex-col lg:flex-row' : 'overflow-y-auto'
          }`}
        >
          <div
            className={`space-y-4 p-5 ${
              expanded ? 'lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 overflow-y-auto' : ''
            }`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={handleNameBlur}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className={`${inputClass} font-mono`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Kind
                </label>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as ElementKind)}
                  className={inputClass}
                >
                  {ELEMENT_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {elementKindLabel(k)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Sort order
                </label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputClass}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Active
            </label>
          </div>

          <div
            className={`flex flex-col min-h-0 p-5 pt-4 ${expanded ? 'flex-1 min-w-0' : 'border-t border-gray-100 dark:border-gray-700/80'}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">HTML body</label>
              <div
                className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50"
                role="tablist"
                aria-label="HTML editor mode"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={htmlPane === 'edit'}
                  onClick={() => setHtmlPane('edit')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    htmlPane === 'edit'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Code className="w-3.5 h-3.5" />
                  Edit HTML
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={htmlPane === 'preview'}
                  onClick={() => setHtmlPane('preview')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    htmlPane === 'preview'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  Preview
                </button>
              </div>
            </div>

            <div className={`flex-1 min-h-0 ${expanded ? '' : ''}`}>
              {htmlPane === 'edit' ? (
                <textarea
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  className={`${inputClass} w-full h-full ${htmlEditorMinH} font-mono text-xs resize-y`}
                  spellCheck={false}
                />
              ) : (
                <div
                  className={`rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-900 overflow-auto ${htmlEditorMinH}`}
                >
                  {bodyHtml.trim() ? (
                    <div className="p-4 sm:p-6 flex justify-center">
                      <div
                        className="w-full max-w-[640px] bg-white text-gray-900 shadow-sm rounded-sm overflow-hidden"
                        dangerouslySetInnerHTML={{ __html: bodyHtml }}
                      />
                    </div>
                  ) : (
                    <p className="p-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No HTML yet — switch to Edit HTML to add content.
                    </p>
                  )}
                </div>
              )}
            </div>
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
              Preview shows how the snippet renders at roughly email width (~640px). Switch back to
              Edit HTML to change the markup.
            </p>
          </div>
        </div>

        <div className="shrink-0 px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2 bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
